import React, { useState } from 'react';
import { AlertCircle, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ConsentFields, { ageFromDob } from './ConsentFields';
import { PROVIDER_NAME } from './SocialSignIn';

/**
 * B-53: the one extra screen a first-time Apple / Google sign-up sees. The
 * account does not exist yet: it is created by POST /auth/social/complete
 * only after the same 18+ date-of-birth gate and terms clickwrap as Register
 * (the server re-checks both). Names arrive pre-filled when the provider
 * shared them (Apple only does so the very first time) and stay editable.
 *
 *   pending — the /auth/social reply { signupTicket, provider, email, firstName, lastName }
 *   onDone(user) — signed in with the new account
 *   onCancel()   — back to the page's normal form
 */
const SocialProfileStep = ({ pending, onDone, onCancel, title = 'One more step' }) => {
  const { completeSocialSignUp } = useAuth();
  const [firstName, setFirstName] = useState(pending.firstName || '');
  const [lastName, setLastName] = useState(pending.lastName || '');
  const [dob, setDob] = useState('');
  const [consents, setConsents] = useState({ analytics: false, marketing: false, audience: false });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const name = PROVIDER_NAME[pending.provider] || 'your account';
  const relay = /@privaterelay\.appleid\.com$/i.test(pending.email || '');

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) { setError('Please enter your first and last name.'); return; }
    const age = ageFromDob(dob);
    if (age === null) { setError('Please enter your date of birth'); return; }
    if (age < 18) { setError('Marakin accounts are for adults aged 18 and over. A parent or guardian can add children and under-18s from their own account.'); return; }
    if (!termsAccepted) { setError('Please accept the Terms of Service to create your account.'); return; }
    setError(''); setLoading(true);
    try {
      const user = await completeSocialSignUp({
        signupTicket: pending.signupTicket, firstName, lastName, dateOfBirth: dob, consents, termsAccepted,
      });
      await onDone(user);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'social_ticket_invalid') setExpired(true);
      setError(err?.response?.data?.error || 'We couldn\'t create your account. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <>
      <h1>{title}</h1>
      <p className="auth-subtitle">
        You're joining Marakin with {name} as <strong>{pending.email}</strong>. Marakin accounts are for
        adults aged 18 and over, so we need your date of birth and your agreement to the terms.
      </p>
      {relay && (
        <p className="social-note">
          You chose Apple's "Hide my email", so club invites sent to your usual address won't find this
          account. Join your club with its invite link instead.
        </p>
      )}
      {error && <div className="auth-error" role="alert"><AlertCircle size={16} /> {error}</div>}
      {expired ? (
        <button type="button" className="btn btn-primary auth-submit" onClick={onCancel}>Start again</button>
      ) : (
        <form onSubmit={submit} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label className="label">First name</label>
              <input className="input" value={firstName} onChange={e => setFirstName(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="label">Last name</label>
              <input className="input" value={lastName} onChange={e => setLastName(e.target.value)} required />
            </div>
          </div>
          <ConsentFields
            dob={dob}
            onDobChange={(e) => setDob(e.target.value)}
            consents={consents}
            onConsentsChange={setConsents}
            termsAccepted={termsAccepted}
            onTermsChange={setTermsAccepted}
          />
          <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
            {loading ? <span className="spinner" /> : <><UserPlus size={16} /> Create my account</>}
          </button>
        </form>
      )}
      <p className="auth-switch">
        <button type="button" className="social-back" onClick={onCancel}>Back</button>
      </p>
    </>
  );
};

export default SocialProfileStep;
